import { Col, Collapse, Row } from 'antd';
import { useFormikContext } from 'formik';
import { getOr, isNil, keyBy } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../../../shared/permissions';
import { AmountUnit, FinanceProductType } from '../../../../../../api/types';
import { useCompany } from '../../../../../../components/contexts/CompanyContextManager';
import CollapsibleWrapper from '../../../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../../../themes/admin/Fields/FormFields';
import hasPermissions from '../../../../../../utilities/hasPermissions';
import {
    exportFinanceProductTableExcel,
    prepareFirstRow,
    prepareWorkbook,
} from '../../../../../../utilities/newVersionExcel';
import { useFinanceProductDetailContext } from '../../../FinanceProductDetailContext';
import ExportExcel from '../../../shared/ExportExcel';
import ImportExcel from '../../../shared/ImportExcel';
import useFinanceProductOptions from '../../../shared/useFinanceProductOptions';
import { type FinanceProductFormValues } from '../../index';
import EditableTable from '../EditableTable';
import useBalloonReferenceData from './useBalloonReferenceData';
import useTerms from './useTerms';

const { Panel } = Collapse;

const colSpan = { lg: 8, xs: 24 };

const mappedHeaders = [
    { text: 'Variant', key: 'variantName' },
    { text: 'Reference*', key: 'variantId' },
    { text: 'Term*', key: 'term' },
];

const excelColumnKeys = mappedHeaders.map(i => i.text);

const getVariantAndTermKey = ({ variantSuiteId, term }) => `${variantSuiteId}-${term}`;

const BalloonTable = ({ disabled = false }: { disabled: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);
    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();

    const company = useCompany(true);
    const showFinderAssignment =
        company?.availableModules.hasFinderVehicleManagementModules &&
        hasPermissions(company?.permissions, [permissionKind.viewFinderVehicles]);

    const { bank } = useFinanceProductDetailContext();

    const { amountUnitOptions } = useFinanceProductOptions(bank?.module?.companyId, company?.countryCode);
    const { displayName, term, balloon, balloonGfv, type } = values;

    const { table, key, settings, unitIsPercentage } = useMemo(() => {
        switch (type) {
            case FinanceProductType.HirePurchaseWithBalloon:
                return {
                    table: balloon.table,
                    key: 'balloon',
                    settings: balloon,
                    unitIsPercentage: balloon.unit === AmountUnit.Percentage,
                };

            case FinanceProductType.HirePurchaseWithBalloonGfv:
                return {
                    table: balloonGfv.table,
                    key: 'balloonGfv',
                    settings: balloonGfv,
                    unitIsPercentage: balloonGfv.residualValueUnit === AmountUnit.Percentage,
                };

            default:
                throw new Error('Unsupported finance product type for balloon table');
        }
    }, [balloon, balloonGfv, type]);

    const { variantColumns, variantRecords, finderVehicleColumns, finderVehicleRecords } = useBalloonReferenceData(
        key,
        settings,
        disabled
    );

    const terms = useTerms(term);

    const onImport = useCallback(
        async data => {
            const currentRecords = [...variantRecords, ...finderVehicleRecords];
            const tableDataList = data
                .filter(
                    record =>
                        !isNil(record.value) &&
                        currentRecords.some(i => i.variantId === `${record.variantId}`) &&
                        terms.includes(record.term)
                )
                .map(value => ({
                    term: value.term,
                    variantSuiteId: currentRecords.find(i => i.variantId === `${value.variantId}`).variantSuiteId,
                    value: value.value,
                }));
            setFieldValue(`${key}.table`, tableDataList);
        },
        [finderVehicleRecords, key, setFieldValue, terms, variantRecords]
    );

    const exportExcel = useCallback(async () => {
        const workbook = await prepareWorkbook();
        const worksheet = workbook.getWorksheet(1);

        prepareFirstRow(worksheet, excelColumnKeys, terms);
        const leaseTableDataByVariantAndTerm = keyBy(getVariantAndTermKey, table || []);
        [...variantRecords, ...finderVehicleRecords].forEach(row => {
            const { variantSuiteId, variantId, variantName } = row;
            const values = terms.map(term => {
                const variantAndTermKey = getVariantAndTermKey({ variantSuiteId, term });

                return getOr(null, `${variantAndTermKey}.value`, leaseTableDataByVariantAndTerm);
            });
            worksheet.addRow([variantName, variantId, ...values]);
        });
        exportFinanceProductTableExcel(workbook, displayName, 'Balloon');
    }, [table, variantRecords, finderVehicleRecords, displayName, terms]);

    return (
        <CollapsibleWrapper defaultActiveKey={[key]}>
            <Panel key={key} header={t('financeProductDetails:tables.balloon.title')}>
                {type === FinanceProductType.HirePurchaseWithBalloon && (
                    <Row gutter={[16, 16]}>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('financeProductDetails:fields.balloonUnit', { returnObjects: true })}
                                disabled={disabled}
                                name={`${key}.unit`}
                                options={amountUnitOptions}
                                required
                                showSearch
                            />
                        </Col>
                    </Row>
                )}
                {type === FinanceProductType.HirePurchaseWithBalloonGfv && (
                    <Row gutter={[16, 16]}>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('financeProductDetails:fields.residualValueUnit', { returnObjects: true })}
                                disabled={disabled}
                                name={`${key}.residualValueUnit`}
                                options={amountUnitOptions}
                                required
                                showSearch
                            />
                        </Col>
                    </Row>
                )}
                <EditableTable columns={variantColumns} records={variantRecords} unitIsPercentage={unitIsPercentage} />
                {showFinderAssignment && (
                    <EditableTable
                        columns={finderVehicleColumns}
                        records={finderVehicleRecords}
                        unitIsPercentage={unitIsPercentage}
                    />
                )}
            </Panel>
            {!disabled && (
                <>
                    <ImportExcel
                        mappedHeaders={mappedHeaders}
                        onImport={onImport}
                        tableType="matrix"
                        title={t('financeProductDetails:importModal.titles.balloon')}
                    />
                    <ExportExcel onClick={exportExcel} />
                </>
            )}
        </CollapsibleWrapper>
    );
};

export default BalloonTable;
